'use client';

import { addMilliseconds } from 'date-fns';
import { throttle } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilState } from 'recoil';
import { needsRefreshState, nextRefreshState } from 'lib/state';
import { MIN_REFRESH_INTERVAL, GLOBAL_REFRESH_INTERVAL } from 'lib/constants';

const REDRAW_INTERVAL = 32; // 32ms is 30fps

export default function GlobalRefreshIndicator({ userCanRefresh = false }) {
  const [, setNeedsRefresh] = useRecoilState(needsRefreshState);
  const [nextRefreshIn, setNextRefresh] = useRecoilState(nextRefreshState);
  const [remaining, setRemaining] = useState(100);

  const redraw = useCallback(() => {
    return setRemaining(((nextRefreshIn - new Date().getTime()) / GLOBAL_REFRESH_INTERVAL) * 100);
  }, [nextRefreshIn, setRemaining]);

  const refresh = useCallback(() => {
    if (!userCanRefresh) {
      return;
    }
    setNextRefresh(addMilliseconds(new Date(), GLOBAL_REFRESH_INTERVAL).getTime());
    setNeedsRefresh(new Date().getTime());
  }, [setNextRefresh, setNeedsRefresh, userCanRefresh]);

  const throttledRefresh = useMemo(
    () => throttle(refresh, MIN_REFRESH_INTERVAL, { leading: true, trailing: false }),
    [refresh]
  );

  useEffect(() => {
    const intervalId = setInterval(redraw, REDRAW_INTERVAL);
    return () => clearInterval(intervalId);
  }, [redraw]);

  const style = {
    '--value': remaining,
    '--size': '2em',
    '--thickness': '4px',
  };

  const remainingSecs = parseInt((nextRefreshIn - new Date().getTime()) / 1000, 10);
  return (
    <div className="tooltip tooltip-left" data-tip={`Data refresh in ${remainingSecs} seconds`}>
      <button
        className="btn bg-base-200 border-base-content border-semitrans btn-square"
        onClick={throttledRefresh}
      >
        <div className="radial-progress text-primary" style={style}></div>
      </button>
    </div>
  );
}
